import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Package, CreditCard, ArrowRight, ShoppingCart, Users, AlertTriangle, Gift } from 'lucide-react';
import BottomNav from '../components/common/BottomNav';
import NotificationBell from '../components/common/NotificationBell';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import { generateDemoGroups } from '../utils/groupData';
import { calculateLeaveImpact, formatLeaveMessage, generateGroupNotifications, trackLeaveEvent } from '../utils/leaveGroupCalculations';
import { formatReliabilityScore } from '../utils/trustCalculations';

const GroupsPage = () => {
  const navigate = useNavigate();
  const { addToCart, isInCart, getCartTotals } = useCart();
  const { addNotification } = useNotification();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userLocation, setUserLocation] = useState(null);
  const [userJoinedGroups, setUserJoinedGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get user location (same as HomePage)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            city: "Sacramento",
            country: "USA",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
        },
        (error) => {
          setUserLocation({
            city: "Sacramento",
            country: "USA", 
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
        }
      );
    } else {
      setUserLocation({
        city: "Sacramento",
        country: "USA",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, []);

  // Load joined groups from localStorage and match with group data
  useEffect(() => {
    if (!userLocation) return;

    const loadJoinedGroups = () => {
      setLoading(true);
      
      // Get joined group IDs from localStorage
      const joinedGroupIds = localStorage.getItem('joinedGroups');
      if (!joinedGroupIds) {
        setUserJoinedGroups([]);
        setLoading(false);
        return;
      }

      const groupIds = JSON.parse(joinedGroupIds);
      
      // Get all available groups
      const allGroups = generateDemoGroups(userLocation);
      
      // Filter to only show joined groups
      const joinedGroups = allGroups.filter(group => groupIds.includes(group.id));
      
      // Add user-specific data (this would come from backend in real app)
      const enhancedGroups = joinedGroups.map(group => {
        // Get user's cart data if available
        const cartData = localStorage.getItem(`cart_${group.id}_data`);
        const userQuantity = cartData ? JSON.parse(cartData).quantity : 1;
        
        return {
          ...group,
          // User-specific data
          userQuantity: userQuantity,
          userTokens: calculateTokenReward(group.tokenReward, userQuantity),
          joinedDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000), // Random date within last week
          deliveryDate: formatDeliveryDate(group.endDate, 3), // 3 days after group closes
          chargeDate: formatDeliveryDate(group.endDate, 1), // 1 day after group closes
        };
      });
      
      setUserJoinedGroups(enhancedGroups);
      setLoading(false);
    };

    loadJoinedGroups();
    
    // Set up listener for storage changes (when user joins new groups in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'joinedGroups') {
        loadJoinedGroups();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event from same tab
    window.addEventListener('groupJoined', loadJoinedGroups);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('groupJoined', loadJoinedGroups);
    };
  }, [userLocation]);

  // Real-time countdown updates
  useEffect(() => {
    const updateFrequency = () => {
      // Check if any group needs urgent updates
      const hasUrgentGroup = userJoinedGroups.some(group => {
        const now = new Date();
        const difference = group.endDate - now;
        const hoursLeft = difference / (1000 * 60 * 60);
        return hoursLeft <= 1 && hoursLeft > 0;
      });
      
      return hasUrgentGroup ? 1000 : 60000; // 1 second if urgent, 1 minute otherwise
    };
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, updateFrequency());

    return () => clearInterval(timer);
  }, [userJoinedGroups]);

  // Calculate token rewards with bulk bonuses
  const calculateTokenReward = (baseTokens, quantity) => {
    let bonus = 1;
    if (quantity >= 5) bonus = 1.5;
    else if (quantity >= 3) bonus = 1.25;
    return Math.floor(baseTokens * quantity * bonus);
  };

  // Format delivery date
  const formatDeliveryDate = (endDate, daysAfter) => {
    const deliveryDate = new Date(endDate);
    deliveryDate.setDate(deliveryDate.getDate() + daysAfter);
    return deliveryDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Tiered countdown calculation (same as HomePage)
  const calculateTimeLeft = (endDate) => {
    const now = new Date();
    const difference = endDate - now;
    
    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      const totalHours = difference / (1000 * 60 * 60);
      
      if (totalHours <= 1) {
        return `${minutes} min ${seconds} sec`;
      } else if (totalHours <= 6) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min`;
      } else if (days > 0) {
        return `${days} day${days !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
    }
    return 'COMPLETED';
  };

  // Get urgency styling (same as HomePage)
  const getUrgencyStyle = (endDate) => {
    const now = new Date();
    const difference = endDate - now;
    const hoursLeft = difference / (1000 * 60 * 60);
    
    if (difference <= 0) {
      return "text-green-600 font-semibold";
    } else if (hoursLeft <= 6) {
      return "text-red-600 font-semibold animate-pulse";
    } else if (hoursLeft <= 48) {
      return "text-yellow-600 font-medium";
    } else {
      return "text-gray-500";
    }
  };

  // Get urgency border styling (same as HomePage)
  const getUrgencyBorderStyle = (endDate) => {
    const now = new Date();
    const difference = endDate - now;
    const hoursLeft = difference / (1000 * 60 * 60);
    
    if (difference <= 0) {
      return "border-l-4 border-green-500";
    } else if (hoursLeft <= 6) {
      return "border-l-4 border-red-500";
    } else if (hoursLeft <= 48) {
      return "border-l-4 border-yellow-500";
    } else {
      return "";
    }
  };

  const handleViewGroup = (id) => {
    navigate(`/groups/${id}`);
  };

  const handleBrowseMore = () => {
    navigate('/home');
  };

  const handleAddToCart = (group, e) => {
    e.stopPropagation();
    
    const cartItem = {
      id: group.id,
      groupId: group.id,
      title: group.title,
      description: group.description,
      currentPrice: group.currentPrice,
      image: group.image,
      vendor: group.vendor,
      tokenReward: group.tokenReward,
      category: group.category,
      quantity: group.userQuantity || 1
    };
    
    addToCart(cartItem);
    
    // Save user quantity preference
    localStorage.setItem(`cart_${group.id}_data`, JSON.stringify({
      quantity: group.userQuantity || 1,
      timestamp: Date.now()
    }));
  };

  const handleLeaveGroup = (groupId, groupTitle, group, e) => {
    e.stopPropagation();
    
    // Get user data (in real app from context)
    const mockUser = {
      id: 'user123',
      name: 'Current User',
      tokenBalance: 124,
      reliabilityScore: {
        overall: 4.2,
        components: {
          groupCompletion: 4.5,
          paymentReliability: 4.8,
          gigCompletion: 4.0,
          responseTime: 3.8,
          communityRating: 4.1
        }
      }
    };
    
    // Calculate leave impact
    const userQuantity = group.userQuantity || 1;
    const leaveImpact = calculateLeaveImpact(group, mockUser, userQuantity);
    
    // Show detailed confirmation message
    const confirmMessage = formatLeaveMessage(leaveImpact, groupTitle);
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Apply leave tracking (in real app, this would be server-side)
    if (leaveImpact.timing === 'late' || leaveImpact.timing === 'veryLate') {
      addNotification({
        type: 'info',
        title: leaveImpact.title,
        message: leaveImpact.message,
        showToast: true
      });
    }
    
    // Show what they're missing by leaving
    if (leaveImpact.tokensForStaying > 0) {
      addNotification({
        type: 'info',
        title: 'Missing Out on Tokens',
        message: `You would have earned ${leaveImpact.tokensForStaying} tokens by staying!`,
        showToast: true
      });
    }
    
    // Update user data with leave tracking
    if (leaveImpact.reputationImpact !== 0) {
      const updatedUser = trackLeaveEvent(mockUser, leaveImpact);
      
      addNotification({
        type: 'info',
        title: 'Reliability Score Updated',
        message: `Your reliability score is now ${formatReliabilityScore(updatedUser.reliabilityScore.overall)}`,
        showToast: true
      });
    }
    
    // Generate group notifications (in real app, sent to all members)
    const groupNotifications = generateGroupNotifications(
      leaveImpact.groupImpact,
      mockUser.name,
      groupTitle
    );
    
    // Remove from joined groups
    const joinedGroupIds = localStorage.getItem('joinedGroups');
    if (joinedGroupIds) {
      const groupIds = JSON.parse(joinedGroupIds);
      const updatedIds = groupIds.filter(id => id !== groupId);
      localStorage.setItem('joinedGroups', JSON.stringify(updatedIds));
      
      // Remove from cart if present
      if (isInCart(groupId, groupId)) {
        // Find the cart item and remove it
        const cartItems = JSON.parse(localStorage.getItem(`cart_${localStorage.getItem('userId') || 'default'}`) || '[]');
        const updatedCart = cartItems.filter(item => item.groupId !== groupId);
        localStorage.setItem(`cart_${localStorage.getItem('userId') || 'default'}`, JSON.stringify(updatedCart));
      }
      
      // Clear any saved cart data for this group
      localStorage.removeItem(`cart_${groupId}_data`);
      
      // Update local state
      setUserJoinedGroups(prev => prev.filter(g => g.id !== groupId));
      
      // Dispatch event so HomePage updates
      window.dispatchEvent(new Event('groupLeft'));
      
      // Show final notification
      addNotification({
        type: 'info',
        title: 'Left Group',
        message: `You've left "${groupTitle}". ${leaveImpact.message}`,
        showToast: true
      });
    }
  };

  const handleViewCart = () => {
    navigate('/cart');
  };

  // Check if group is completed
  const isGroupCompleted = (group) => {
    const now = new Date();
    return group.isCompleted || group.endDate <= now || group.progress >= 100;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 pb-20">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your groups...</p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pb-20">
      {/* Consistent Header with Notifications and Tokens */}
      <div className="flex justify-between items-center mt-6 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Your Groups</h1>
          <p className="text-gray-600 text-sm mt-1">
            {userJoinedGroups.length} active group{userJoinedGroups.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center">
          <button 
            onClick={handleViewCart}
            className="relative mr-3 p-2 hover:bg-gray-100 rounded-full"
          >
            <ShoppingCart size={20} />
            {getCartTotals().itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {getCartTotals().totalItems}
              </span>
            )}
          </button>
          <NotificationBell />
          <div className="flex items-center bg-yellow-100 rounded-full px-3 py-1 ml-3">
            <span className="font-bold mr-1">124</span>
            <span className="text-yellow-600">⭐</span>
          </div>
        </div>
      </div>
      
      {/* Reliability Score Display */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Your Reliability Score</h3>
            <div className="flex items-center">
              {formatReliabilityScore(4.2, true).split(' ').slice(0, -1).join(' ')}
              <span className="ml-2 text-lg font-bold text-gray-800">4.2</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Level</div>
            <div className="text-sm font-medium text-gray-700">Good</div>
          </div>
        </div>
        {userJoinedGroups.filter(g => !isGroupCompleted(g)).length > 0 && (
          <div className="mt-3 text-xs text-gray-600 flex items-center">
            <AlertTriangle size={12} className="mr-1 text-yellow-500" />
            Leaving groups late affects your score
          </div>
        )}
      </div>

      {/* User's Joined Groups */}
      {userJoinedGroups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Users size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">No Groups Joined Yet</h2>
          <p className="text-gray-600 mb-6">
            Join groups to start saving with your community!
          </p>
          <button 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            onClick={handleBrowseMore}
          >
            Browse Available Groups
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {userJoinedGroups.map((group) => {
            const groupCompleted = isGroupCompleted(group);
            
            return (
              <div 
                key={group.id}
                className={`bg-white rounded-lg shadow overflow-hidden cursor-pointer ${getUrgencyBorderStyle(group.endDate)}`}
                onClick={() => handleViewGroup(group.id)}
              >
                <div className="h-32 bg-gray-200 flex items-center justify-center relative overflow-hidden">
                  <img 
                    src={group.image} 
                    alt={group.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <span className="text-gray-500 absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
                    Product Image
                  </span>
                  
                  {/* Status Badges */}
                  <div className="absolute top-2 left-2 flex gap-2">
                    <div className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                      YOUR ORDER
                    </div>
                    {group.userQuantity >= 3 && (
                      <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                        BULK HERO
                      </div>
                    )}
                    {groupCompleted && (
                      <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                        COMPLETED
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-lg">{group.title}</h3>
                      <div className="text-xs text-gray-500">{group.vendor}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-blue-600">
                        Qty: {group.userQuantity}
                      </div>
                      <div className="text-xs text-gray-500">
                        {group.userTokens} tokens
                      </div>
                    </div>
                  </div>

                  {/* Price Info */}
                  <div className="flex justify-between text-sm mb-2">
                    <div>Your Price: <strong>${(group.currentPrice * group.userQuantity).toFixed(2)}</strong></div>
                    <div className="text-green-600">Save {group.savings}%</div>
                  </div>

                  {/* Group Progress */}
                  <div className="text-sm text-blue-600 mb-2">
                    Next break: ${group.nextBreakPrice} at {group.nextBreakCount} units
                  </div>
                  
                  <div className="bg-gray-200 h-2 rounded-full mb-2">
                    <div 
                      className={`h-2 rounded-full ${groupCompleted ? 'bg-green-500' : 'bg-blue-600'}`}
                      style={{ width: `${Math.min(group.progress, 100)}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-500 mb-3">
                    <div>{group.currentUnits}/{group.nextBreakCount} units ({group.currentCount} people)</div>
                    <div className={getUrgencyStyle(group.endDate)}>
                      {groupCompleted ? 'Group Completed!' : `Closes in ${calculateTimeLeft(group.endDate)}`}
                    </div>
                  </div>

                  {/* Payment & Delivery Timeline */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <CreditCard size={14} className="text-orange-600" />
                        </div>
                        <div className="font-medium text-gray-700">Charge</div>
                        <div className="text-gray-500">{group.chargeDate}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Package size={14} className="text-blue-600" />
                        </div>
                        <div className="font-medium text-gray-700">Delivery</div>
                        <div className="text-gray-500">{group.deliveryDate}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Calendar size={14} className="text-green-600" />
                        </div>
                        <div className="font-medium text-gray-700">Joined</div>
                        <div className="text-gray-500">
                          {Math.floor((Date.now() - group.joinedDate) / (1000 * 60 * 60 * 24))}d ago
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pickup Location */}
                  <div className="bg-blue-50 rounded p-2 mb-3 text-xs text-blue-800">
                    📍 Pickup: {group.pickupLocation}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button 
                      className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium flex items-center justify-center hover:bg-gray-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewGroup(group.id);
                      }}
                    >
                      View Details
                      <ArrowRight size={16} className="ml-1" />
                    </button>
                    
                    {!groupCompleted && (
                      <button
                        onClick={(e) => handleAddToCart(group, e)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center transition-colors ${
                          isInCart(group.id, group.id)
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        <ShoppingCart size={16} className="mr-1" />
                        {isInCart(group.id, group.id) ? 'In Cart' : 'Add to Cart'}
                      </button>
                    )}
                  </div>
                  
                  {/* Leave Group Option with Encouragement */}
                  {!groupCompleted && (
                    <div>
                      {/* Show bonus for staying if close to deadline */}
                      {calculateTimeLeft(group.endDate).includes('hour') && (
                        <div className="flex items-center text-xs text-green-600 mb-2 bg-green-50 p-2 rounded">
                          <Gift size={14} className="mr-1" />
                          Stay to earn completion bonus tokens!
                        </div>
                      )}
                      {/* Bulk hero reminder */}
                      {group.userQuantity >= 3 && (
                        <div className="flex items-center text-xs text-blue-600 mb-2 bg-blue-50 p-2 rounded">
                          <AlertTriangle size={14} className="mr-1" />
                          You're a Bulk Hero helping everyone save!
                        </div>
                      )}
                      <button
                        onClick={(e) => handleLeaveGroup(group.id, group.title, group, e)}
                        className="w-full text-gray-600 hover:text-gray-700 text-sm font-medium py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Leave Group
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Browse More Groups */}
      {userJoinedGroups.length > 0 && (
        <button 
          className="w-full bg-blue-600 text-white py-3 rounded-lg mt-6 font-semibold hover:bg-blue-700 transition-colors"
          onClick={handleBrowseMore}
        >
          Browse More Groups
        </button>
      )}

      <BottomNav />
    </div>
  );
};

export default GroupsPage;